/**
 * WHERE THINGS GO ON A PDF PAGE — `tasks/pdf-letterhead-placement.md` §2.1.
 *
 * A PURE module: no jsPDF, no DOM, no Angular, no clock. Every number is millimetres, because the
 * document is built with `unit: 'mm'` and mixing units is the kind of mistake that produces a
 * plausible-looking page with everything 3× too big.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────────────────────
 *
 * The layout used to be literals scattered through two duplicated 160-line methods: `y = 55` in
 * four places, `pageHeight - 55` in two, and a SEPARATE `50`/`50` pair inside the media embedder
 * that never agreed with them. None of it was conditional on whether a letterhead was actually
 * drawn, so a page without one still reserved 55mm of empty space at the top for it.
 *
 * That was harmless while every page carried the same chrome. It stops being harmless the moment a
 * document has a cover page and then bare pages — which is exactly what a returned practice
 * document is (§3.1 of the task doc).
 *
 * ── THE ONE SEAM ─────────────────────────────────────────────────────────────────────────────
 *
 * `ChromePredicate` answers "does page N of M carry a letterhead / a footer", and BOTH the layout
 * maths and the final drawing pass read it. They cannot disagree about which pages have chrome,
 * because there is only one answer and both ask the same question.
 *
 * `coverPagePredicate` is the returned-document shape. `chromePredicate` — the every / first-page /
 * last-page variant behind the export menu — arrives with Phase B and is the same function shape,
 * which is why the cover page and the placement options are one mechanism rather than two.
 */

/** A page's dimensions in mm. A PARAMETER, never a constant — a cover page may be portrait and a
 *  landscape scan the page after it (§3.2 of the task doc). */
export interface PageDims {
  width: number;
  height: number;
}

export const A4_PORTRAIT: PageDims = { width: 210, height: 297 };
export const A4_LANDSCAPE: PageDims = { width: 297, height: 210 };

/**
 * The letterhead band. `imageY + imageHeight + gapBelow` is 55 — the literal that used to be
 * written out four times, and the number every existing PDF is laid out against.
 */
export const LETTERHEAD = { imageY: 5, imageHeight: 35, gapBelow: 15 };

/**
 * The footer band, measured up from the bottom of the page.
 *
 * `imageTopOffset + gapAbove` is 55, mirroring the letterhead — again the existing literal.
 * `textBaseline` / `textOnlyBaseline` are where the "Generated by…" line and the page number sit,
 * with and without a footer image behind them.
 */
export const FOOTER = {
  imageHeight: 35,
  imageTopOffset: 45,
  gapAbove: 10,
  textBaseline: 5,
  textOnlyBaseline: 10,
};

/**
 * `topNoLetterhead` and `bottomNoFooter` are the NEW numbers — what a page reclaims when it carries
 * no chrome. `bottomNoFooter` is 20 rather than something smaller because the page number is still
 * drawn at `textOnlyBaseline` on every page (task doc §0.6), and content must not run into it.
 */
export const MARGIN = { left: 20, right: 20, topNoLetterhead: 15, bottomNoFooter: 20 };

/**
 * The media embedder's own reserves.
 *
 * DELIBERATELY 50, NOT 55. The 5mm discrepancy against the text flow is pre-existing, and
 * unifying it would change the output of every PDF that embeds media — which the task doc's §0.3
 * forbids. It is recorded here rather than silently corrected so the next person sees that it was
 * noticed and left alone.
 */
export const MEDIA = { topWithLetterhead: 50, bottomWithFooter: 50, gap: 4, sideMargin: 10 };

/** Whether one page carries a letterhead and/or a footer. */
export interface PageChrome {
  header: boolean;
  footer: boolean;
}

/** `(pageIndex 1-based, totalPages) => PageChrome`. The single seam. See the header. */
export type ChromePredicate = (pageIndex: number, totalPages: number) => PageChrome;

/**
 * Every page chromed — what the product has always done, and the default for everything that has
 * not opted into something else.
 */
export const ALL_CHROME: ChromePredicate = () => ({ header: true, footer: true });

/**
 * No page chromed at all - the returned document WITHOUT its cover (`pdf-download.service.ts`).
 *
 * `coverPagePredicate(0)` cannot express this: it floors a non-positive count back to 1, because
 * a returned document always had a cover until the export menu learned to drop it. Rather than
 * loosen that guard - which exists so a miscounted cover never silently loses its letterhead -
 * the "no cover" case gets its own predicate and says so.
 */
export const NO_CHROME: ChromePredicate = () => ({ header: false, footer: false });

/**
 * The returned-document shape: a cover, then bare pages.
 *
 * `coverPages` is a COUNT and not always 1: the clinician's attachments flow onto the cover and may
 * overflow onto a second or third page, and those are cover-continuation pages that legitimately
 * keep their chrome. The caller counts them AFTER the cover has finished growing.
 *
 * Pages beyond the cover get no header, no footer and — because the drawing pass reads the same
 * predicate — no page number either. A full-bleed clinical scan must not be overprinted with
 * anything.
 */
export function coverPagePredicate(coverPages: number): ChromePredicate {
  const covers = Number.isFinite(coverPages) && coverPages > 0 ? Math.floor(coverPages) : 1;
  return (pageIndex: number) => {
    const chromed = pageIndex <= covers;
    return { header: chromed, footer: chromed };
  };
}

/** The first baseline content may occupy on a page. */
export function contentTop(chrome: PageChrome): number {
  return chrome.header
    ? LETTERHEAD.imageY + LETTERHEAD.imageHeight + LETTERHEAD.gapBelow   // 55
    : MARGIN.topNoLetterhead;                                            // 15
}

/** The last baseline content may occupy on a page. */
export function contentBottom(chrome: PageChrome, page: PageDims): number {
  return chrome.footer
    ? page.height - (FOOTER.imageTopOffset + FOOTER.gapAbove)             // height - 55
    : page.height - MARGIN.bottomNoFooter;                               // height - 20
}

/**
 * The width text may wrap within.
 *
 * THE OLD VALUE WAS `pageWidth + 30` — 240mm on a 210mm page. Wrapping was therefore effectively
 * disabled: `splitTextToSize` only ever broke on explicit newlines, and a long paragraph ran off
 * the right edge of the page and was clipped by the renderer. Nothing errored; the text was simply
 * not there.
 *
 * Correcting it changes the line breaks of every PDF this product has ever produced, which is why
 * it lands in its own commit (task doc §1.5).
 */
export function textWrapWidth(page: PageDims): number {
  return page.width - MARGIN.left - MARGIN.right;
}

/** Where to draw an image so it fills a page without cropping. */
export interface FullPagePlacement {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * CONTAIN, never crop, never overflow.
 *
 * A returned document is a patient's completed clinical paperwork. Cropping to fill the page would
 * lose whatever sits at the edge — a signature, a date, the page number the patient wrote — and it
 * would do so invisibly. So the image is scaled to fit entirely and centred, and a landscape scan
 * on a portrait page gets white bands above and below rather than losing its left and right.
 *
 * `bleedMargin` is 0 by default for a true full-page fit; pass a few mm if a printer clips.
 */
export function fitFullPage(
  src: { width: number; height: number },
  page: PageDims,
  bleedMargin = 0,
): FullPagePlacement {
  const availableWidth = page.width - (2 * bleedMargin);
  const availableHeight = page.height - (2 * bleedMargin);

  // A source with a non-positive dimension cannot be scaled; give it the whole page rather than
  // dividing by zero and producing NaN coordinates that jsPDF renders as a blank page.
  if (!(src.width > 0) || !(src.height > 0)) {
    return { x: bleedMargin, y: bleedMargin, width: availableWidth, height: availableHeight };
  }

  const scale = Math.min(availableWidth / src.width, availableHeight / src.height);
  const width = src.width * scale;
  const height = src.height * scale;

  return {
    x: (page.width - width) / 2,
    y: (page.height - height) / 2,
    width,
    height,
  };
}

/**
 * Which orientation a page should take for one image.
 *
 * A markedly landscape scan on a portrait page shrinks to about half the height it could have, so
 * it gets a landscape page instead. The 1.2 threshold keeps near-square images on the portrait page
 * they belong on rather than flipping orientation for a 5% difference.
 *
 * Per-page orientation is used rather than jsPDF's `addImage` rotation argument: that argument's
 * pivot is the placement box origin, differs between jsPDF versions, and puts the page off-canvas
 * with no error when it is wrong.
 */
export function pageDimsForImage(src: { width: number; height: number }): PageDims {
  if (!(src.width > 0) || !(src.height > 0)) return A4_PORTRAIT;
  return src.width / src.height > 1.2 ? A4_LANDSCAPE : A4_PORTRAIT;
}
